import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';

import { getDateFnsLocale } from '../../shared/utils/get-date-fns-locale.util';
import { LocalStorageItem } from '../../core/constants';
import { DateTimeService } from '../../core/services/date-time/date-time.service';
import { STORAGE_SCHEMA } from '../../core/services/local-storage/local-storage.schema';
import { LocalStorageService } from '../../core/services/local-storage/local-storage.service';
import type { User } from '../models/user';
import { selectCurrentUser } from '../store';
import { setCurrentUser } from '../store/user-info/user-info.actions';

/**
 * The signed-in user for the lifetime of the session. The store is the source of truth;
 * localStorage only holds the seed `AppInitService` reads once at boot, so a teardown that
 * clears storage cannot pull the user out from under views that are still attached.
 */
@Injectable({ providedIn: 'root' })
export class CurrentUserService {
  private store = inject<Store>(Store);
  private localStorage = inject(LocalStorageService);
  private dateTimeService = inject(DateTimeService);

  readonly user$ = this.store.select(selectCurrentUser);

  /** `undefined` until the boot seed lands, and on every unauthenticated route. */
  readonly currentUser = toSignal(this.user$, { requireSync: true });

  /**
   * Throws when read outside an authenticated view — the few callers that legitimately run
   * before login (the error handler, the ticketing interceptor) catch it.
   */
  get user(): User {
    const user = this.currentUser();
    if (!user) {
      throw new Error('No current user: read outside an authenticated session');
    }
    return user;
  }

  /** Updates the session and the seed the next boot reads. */
  set(user: User): void {
    this.store.dispatch(setCurrentUser({ user }));
    this.dateTimeService.locale = getDateFnsLocale(user.locale);
    this.localStorage.setItem(LocalStorageItem.User, STORAGE_SCHEMA.user.codec.write(user));
  }
}
